import { Box, Text } from '@factorialco/deprecated-design-system'
import { FormikErrors, FormikProps } from 'formik'

import DayPicker from 'modules/core/components/DayPicker'
import TextField from 'modules/core/components/TextField'
import { dateBackendToUI, dateUIToBackend } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import DialCodeSelector from 'modules/expenses/components/common/DialCodeSelector'

import { CreateCardForm } from './useCardForm'

type Props = {
  form: FormikProps<CreateCardForm>
  locale: string
}

const setValue = (
  field: keyof CreateCardForm['cardholderData'],
  value: string,
  form: Props['form'],
  validate = false
) => {
  form.setValues(
    {
      ...form.values,
      cardholderData: { ...form.values.cardholderData, [field]: value },
    },
    validate
  )
}

const getError = (
  field: keyof FormikErrors<CreateCardForm['cardholderData']>,
  form: Props['form']
) => {
  return form.touched.cardholderData?.[field] || form.values.cardholderData?.[field] != ''
    ? form.errors.cardholderData?.[field]
    : undefined
}

const setTouched = (
  field: keyof CreateCardForm['cardholderData'],
  form: Props['form'],
  value = false
) => {
  form.setTouched(
    {
      ...form.touched,
      cardholderData: { ...form.touched.cardholderData, [field]: true },
    },
    value
  )
}

export const CardholderForm = ({ form, locale }: Props) => {
  return (
    <>
      <Box flexDirection="column" gap="s8">
        <Box flexDirection="row" gap="s16">
          <TextField
            mandatory
            label={i18n.t('expenses_v2.forms.fields.first_name.label')}
            value={form.values.cardholderData.firstName}
            error={getError('firstName', form)}
            onBlur={(ev) => {
              setTouched('firstName', form)
              form.handleBlur(ev)
            }}
            onChange={(value) => setValue('firstName', value, form)}
          />
          <TextField
            label={i18n.t('expenses_v2.forms.fields.last_name.label')}
            mandatory
            value={form.values.cardholderData.lastName}
            error={getError('lastName', form)}
            onBlur={(ev) => {
              setTouched('lastName', form)
              form.handleBlur(ev)
            }}
            onChange={(value) => setValue('lastName', value, form)}
          />
        </Box>
        <Text color="grey700" size="200">
          {i18n.t('expenses_v2.forms.fields.first_name.info')}
        </Text>
      </Box>
      <DayPicker
        label={i18n.t('software_management.initial_setup.wizard.cardholder_form.birth_date')}
        onChange={(value) => {
          setValue('dateOfBirth', dateUIToBackend(value), form, true)
          setTouched('dateOfBirth', form, false)
        }}
        onToggle={(isOpen) => (!isOpen ? setTouched('dateOfBirth', form) : undefined)}
        value={
          form.values.cardholderData.dateOfBirth
            ? dateBackendToUI(form.values.cardholderData.dateOfBirth)
            : ''
        }
        mandatory
        error={getError('dateOfBirth', form)}
      />
      <Box flexDirection="row" gap="s16">
        <Box gap="s6" width="fit-content" flexNoShrink>
          <DialCodeSelector
            label={i18n.t('software_management.initial_setup.wizard.cardholder_form.country_code')}
            onChange={(value) => {
              setValue('phoneCountryCode', value, form, true)
              setTouched('phoneCountryCode', form, false)
            }}
            onClose={() => setTouched('phoneCountryCode', form, true)}
            value={form.values.cardholderData.phoneCountryCode}
            locale={locale}
            mandatory
            error={getError('phoneCountryCode', form)}
          />
          <Text color="grey700" size="200">
            {i18n.t('expenses_v2.forms.fields.sending_options.country_phone_number_example')}
          </Text>
        </Box>
        <Box gap="s6" width="full">
          <TextField
            label={i18n.t('software_management.initial_setup.wizard.cardholder_form.phone_number')}
            onChange={(value) => setValue('phoneNumber', value, form)}
            onBlur={(ev) => {
              setTouched('phoneNumber', form, true)
              form.handleBlur(ev)
            }}
            value={form.values.cardholderData.phoneNumber}
            mandatory
            error={getError('phoneNumber', form)}
          />
          <Text color="grey700" size="200">
            {i18n.t('expenses_v2.forms.fields.mobile_phone_number.info')}
          </Text>
        </Box>
      </Box>
    </>
  )
}
